import { useEffect, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { apiNameToTrialIds } from 'self-service-api/constants/Trials';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { AdminModalTypes } from '../../../constants/adminModalTypes';
import { useGlobalAlertStore } from '../../../hooks/useGlobalAlertStore';
import { NEXT_STEPS_SURVEY_API_NAMES, TRIAL_STANDARD_DURATION, proDefaultSurveyResponseMap } from '../../../constants/trial';
import { useIsAdmin } from '../../../hooks/useIsAdmin';
import { useStartTrial } from '../../../hooks/useStartTrial';
import { useTrialState } from '../../../hooks/useTrialState';
import { trackPurchaseMotionFailure, trackTrialActivation } from '../../../utils/metrics';
import { handleTrialStartSuccess } from '../../../utils/trials';
import { isUpgradePoint } from '../../../utils/ums';
import { getTrackingProperties, track } from '../../../utils/usageTracker';
import { AdminModal } from '../../AdminModal/AdminModal';
import { ErrorBoundary, ErrorBoundaryNames } from '../../ErrorBoundary';
import { ActivationErrorModal } from './ActivationErrorModal';
import { ActivationSuccessModal } from './ActivationSuccessModal';
import { ConflictingTrialToolTip } from './ConflictingTrialToolTip';
import { EnterpriseNextStepsSurvey } from './EnterpriseNextStepsSurvey';
import { ProfessionalFeatureSurvey } from './ProfessionalFeatureSurvey';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SUCCESS_MODAL_TYPES = {
  SUCCESS: 'SUCCESS',
  NEXT_STEPS_SURVEY: 'NEXT_STEPS_SURVEY',
  FEATURE_SURVEY: 'FEATURE_SURVEY'
};
const TrialButtonBase = ({
  apiName,
  level,
  location,
  trackingData,
  duration,
  uiButtonProps,
  children,
  upgradeConfig,
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId,
  onClick
}) => {
  const {
    error,
    loading,
    trialState: {
      conflictingTrials,
      activeTrials,
      expiredTrials
    }
  } = useTrialState(apiName);
  const [successModalType, setSuccessModalType] = useState();
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const {
    alertStore,
    shouldRenderGlobalList
  } = useGlobalAlertStore();
  const trialId = apiNameToTrialIds[apiName];
  const trialTrackingProperties = useMemo(() => ({
    activeTrials: [...activeTrials],
    expiredTrials: [...expiredTrials],
    isMultiTrial: activeTrials.size > 1,
    trialId,
    duration: duration || TRIAL_STANDARD_DURATION
  }), [activeTrials, expiredTrials, duration, trialId]);
  const trackingProperties = useMemo(() => getTrackingProperties({
    apiName,
    trackingData,
    upgradeConfig
  }), [apiName, trackingData, upgradeConfig]);
  const [startTrial, {
    data,
    error: startTrialError,
    loading: startTrialLoading
  }] = useStartTrial();
  const isAdmin = useIsAdmin();
  useEffect(() => {
    if (!loading) {
      track('trialActivationButtonInteraction', Object.assign({
        action: 'viewed'
      }, trackingProperties, trialTrackingProperties));
      if (isUpgradePoint(location)) {
        track('interaction', Object.assign({
          action: 'viewed trial activation'
        }, trackingProperties, {
          isPrimaryCta: level === 1,
          isSecondaryCta: level === 2
        }));
      }
    }
  }, [loading, level, location, trackingProperties, trialTrackingProperties]);
  useEffect(() => {
    if (startTrialError) {
      trackPurchaseMotionFailure({
        uniqueId: trackingData.uniqueId,
        apiName,
        purchaseMotion: 'TRIAL'
      });
      track('trialModalInteraction', Object.assign({
        action: 'opening activation error modal'
      }, trackingProperties, trialTrackingProperties));
    }
  }, [startTrialError, apiName, trackingData.uniqueId, trackingProperties, trialTrackingProperties]);
  useEffect(() => {
    if (data) {
      if (proDefaultSurveyResponseMap[apiName]) {
        setSuccessModalType(SUCCESS_MODAL_TYPES.FEATURE_SURVEY);
      } else {
        setSuccessModalType(SUCCESS_MODAL_TYPES.SUCCESS);
      }
      track('trialModalInteraction', Object.assign({
        action: 'opening activation success modal'
      }, trackingProperties, trialTrackingProperties));
    }
  }, [apiName, data, trackingProperties, trialTrackingProperties]);
  if (startTrialLoading || loading) {
    return /*#__PURE__*/_jsx(LoadingButton, Object.assign({}, uiButtonProps, {
      loading: true,
      children: children !== null && children !== void 0 ? children : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "purchaseMotions.buttons.trial"
      })
    }));
  }
  const handleClick = () => {
    onClick === null || onClick === void 0 || onClick('TRIAL');
    track('trialActivationButtonInteraction', Object.assign({
      action: 'clicked'
    }, trackingProperties, trialTrackingProperties));
    if (isUpgradePoint(location)) {
      track('interaction', Object.assign({}, trackingProperties, {
        isPrimaryCta: level === 0,
        isSecondaryCta: level === 1
      }));
    }
    if (isAdmin) {
      void startTrial({
        apiName: apiName
      }).then(() => {
        trackTrialActivation({
          app: trackingData.app,
          apiName
        });
        track('trialModalInteraction', Object.assign({
          action: 'opened modal container'
        }, trackingProperties, trialTrackingProperties));
      });
    } else {
      setAdminModalOpen(true);
    }
  };
  const handleStartSuccess = () => {
    const source = `${trackingData.app}-${trackingData.screen}-${trackingData.uniqueId}`;
    if (NEXT_STEPS_SURVEY_API_NAMES.includes(apiName)) {
      setSuccessModalType(SUCCESS_MODAL_TYPES.NEXT_STEPS_SURVEY);
    } else {
      handleTrialStartSuccess({
        apiName: apiName,
        source
      });
    }
  };
  const handleProfessionalSurveySubmit = () => {
    setSuccessModalType(SUCCESS_MODAL_TYPES.SUCCESS);
  };
  const handleTrialAdminModalClose = () => {
    setAdminModalOpen(false);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldRenderGlobalList && /*#__PURE__*/_jsx(FloatingAlertList, {
      "data-test-id": "global-floating-alert-list",
      alertStore: alertStore
    }), !!startTrialError && /*#__PURE__*/_jsx(ActivationErrorModal, {
      apiName: apiName,
      error: startTrialError,
      onStartSuccess: handleStartSuccess
    }), successModalType === SUCCESS_MODAL_TYPES.SUCCESS && /*#__PURE__*/_jsx(ActivationSuccessModal, {
      apiName: apiName,
      onStartSuccess: handleStartSuccess,
      upgradeConfig: upgradeConfig
    }), successModalType === SUCCESS_MODAL_TYPES.FEATURE_SURVEY && /*#__PURE__*/_jsx(ProfessionalFeatureSurvey, {
      apiName: apiName,
      trackingData: trackingData,
      trialTrackingProperties: trialTrackingProperties,
      upgradeConfig: upgradeConfig,
      onSurveySubmit: handleProfessionalSurveySubmit
    }), successModalType === SUCCESS_MODAL_TYPES.NEXT_STEPS_SURVEY && /*#__PURE__*/_jsx(EnterpriseNextStepsSurvey, {
      apiName: apiName,
      trackingData: trackingData,
      trialTrackingProperties: trialTrackingProperties,
      upgradeConfig: upgradeConfig
    }), adminModalOpen && /*#__PURE__*/_jsx(AdminModal, {
      alertStore: alertStore,
      apiName: apiName,
      modalType: AdminModalTypes.REQUEST_TRIAL,
      trackingData: trackingData,
      trialId: trialId,
      upgradeConfig: upgradeConfig,
      onClose: handleTrialAdminModalClose
    }), /*#__PURE__*/_jsx(ConflictingTrialToolTip, {
      apiName: apiName,
      conflictingTrials: conflictingTrials,
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        onClick: handleClick,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        truncate: true,
        truncateStringProps: {
          justifyContent: 'center'
        }
      }, uiButtonProps, {
        disabled: (uiButtonProps === null || uiButtonProps === void 0 ? void 0 : uiButtonProps.disabled) || !!error || !!conflictingTrials.length,
        children: children !== null && children !== void 0 ? children : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.buttons.trial"
        })
      }))
    })]
  });
};
export const TrialButton = props => /*#__PURE__*/_jsx(ErrorBoundary, {
  boundaryName: ErrorBoundaryNames.TRIAL_BUTTON,
  children: /*#__PURE__*/_jsx(TrialButtonBase, Object.assign({}, props))
});